import type {AiImagePanelProps} from '@Panels/ai-image-panel/ai-image-panel.types';
import {AiImageModelOption, MediaTypePrompt, TextToImageGridErrorState} from '@Panels/ai-image-panel/ai-image-panel.types';
import type {MouseEventHandler, ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {HeaderSize, Panel, PanelContent, PanelFooter, PanelFooterPrimaryDefaultAction, PanelHeader} from '@Components/panel';
import type {AITextToImageBackendData, GenerateAIImageResponse, GenerateImageParams} from '@Libraries/pmw-stock-media-library';
import {
  AspectRatio,
  GENERATE_IMAGE_FLAGGED_PROMPT_RESPONSE_CODE,
  GENERATE_IMAGE_INSUFFICIENT_CREDITS_RESPONSE_CODE,
  GENERATE_IMAGE_UNSUPPORTED_LANGUAGE_RESPONSE_CODE,
  GENERATE_IMAGE_VAGUE_PROMPT_RESPONSE_CODE,
  generateImage,
  getRemainingTextToImageUsages,
  setRecentStockItem,
} from '@Libraries/pmw-stock-media-library';
import {v4 as uuidv4} from 'uuid';
import {addItemsToGrid, deleteGridItems, getSelectedGridItems, replaceGridItem, toggleGridItemSelection, updateGridItem} from '@Components/base-grid';
import {getGridItemForAIGeneratedImageV2, uploadSelectedItem} from '@Panels/stock-panel/stock-panel.library';
import {TextToImageGrid} from '@Components/text-to-image-grid';
import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import {getAiSkeletonItem, MIN_STOCK_ITEMS_TO_DISPLAY} from '@Panels/stock-panel/stock-panel.types';
import type {GridSkeletonItemStorage} from '@Components/base-grid/components/grid-skeleton-item';
import {SkeletonAnimationType} from '@Components/base-grid/components/grid-skeleton-item';
import type {GridAiImageItemStorage} from '@Components/base-grid/components/grid-stock-image-item';
import {isUserPremium} from '@Libraries/user.library';
import {getGridIdForPanel, showTextToImageUpsellDialog} from '@Libraries/text-to-image-library';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import {MODAL_IDS, openModal} from '@Components/modal-container-deprecated';
import {setRecentStockAvailability} from '@Panels/stock-panel/stock-panel-reducer';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {isMobile} from 'react-device-detect';
import type {TextToImageGridProps} from '@Components/text-to-image-grid/text-to-image-grid.types';
import {AiImageState} from '@Components/poster-editor/components/poster-ai/poster-ai.types';
import type {BuyCreditsResponse} from '@Components/remove-bg-purchase-modal';
import styles from './ai-image-panel.module.scss';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {updateAiImageState, updateIsUploadingToMyUploads} from '@/components/poster-editor/poster-editor-reducer';
import {IconSize} from '@Components/icon-v2/icon.types';
import {noop} from '@Utils/general.util';
import type {SelectedPromptOptionValues} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import {PromptOption} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import AiImagePromptBox from '@Panels/ai-image-panel/components/ai-image-prompt-box/ai-image-prompt-box';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';

const SELECTION_ID = 'TEXT_TO_IMAGE_POP_OVER_SELECTION';
const PANEL_ID = 'TEXT_TO_IMAGE_PANEL_FALLBACK_ID';

export default function AiImagePanel({populateRecentlyAddedOnItemAdd = true, ...props}: AiImagePanelProps): ReactElement<any> {
  const [errorState, setErrorState] = useState<TextToImageGridErrorState>(TextToImageGridErrorState.NONE);
  const [mediaTypePrompt, setMediaTypePrompt] = useState<MediaTypePrompt>(MediaTypePrompt.PHOTO);
  const [remainingTextToImageUsages, setRemainingTextToImageUsages] = useState<number>(0);
  const selectionIDRef = useRef(props.selectionID ?? SELECTION_ID);
  const gridItemIdsProcessingRef = useRef<Record<string, true>>({});
  const selectedItems = useAppSelector((state) => {
    return getSelectedGridItems(state, selectionIDRef.current);
  });
  const aiImageState = useAppSelector((state) => {
    return state.posterEditor.aiState.aiImage;
  });
  const selectedAiModelId = useAppSelector((state) => {
    return state.userPreferences.selectedAiImageModelId;
  });
  const dispatch = useAppDispatch();
  const panelId = useRef(props.panelId ?? PANEL_ID);
  const gridIDRef = useRef(getGridIdForPanel(panelId.current));
  const aiSkeletonItemIds = useRef<string[]>([]);
  const onClose: () => void = props.onClose
    ? (): void => {
        if (props.onClose) {
          props.onClose();
        }
      }
    : useDefaultPanelClose(panelId.current);

  useEffect(() => {
    getRemainingTextToImageInvocations();
  }, []);

  const getRemainingTextToImageInvocations = (): void => {
    getRemainingTextToImageUsages()
      .then((res) => {
        setRemainingTextToImageUsages(res);
      })
      .catch((e) => {
        console.error(e);
      });
  };

  const onGridItemClicked = (item: GridItemStorage, isDoubleClick: boolean): void => {
    if (isMobile) {
      if (isDoubleClick && selectedItems.length === 1) {
        void onImageClicked([item]);
      } else {
        dispatch(
          toggleGridItemSelection({
            gridItemId: item.id,
            gridSelectionGroupId: selectionIDRef.current,
          })
        );
      }
    } else {
      void onImageClicked([item]);
    }
  };

  const getAIImageGrid = (): ReactElement<any> => {
    const gridProps: TextToImageGridProps = {
      customOnGridItemClicked: onGridItemClicked,
      gridClasses: `spacing-p-t-3 spacing-p-b-3 ${styles.grid}`,
      gridSelectionGroupId: selectionIDRef.current,
      maxSelectionNumber: 1,
      gridId: gridIDRef.current,
      isLoading: aiImageState === AiImageState.GENERATING,
      infographicClasses: styles.infographicImage,
      infographicTextClasses: styles.infographicText,
    };

    return <TextToImageGrid {...gridProps} key={gridIDRef.current} />;
  };

  const getPanelContent = (): ReactElement<any> => {
    return (
      <div className={'spacing-p-l-4 spacing-p-r-1 spacing-p-t-1'}>
        <AiImagePromptBox
          generateImage={onGenerateImage}
          errorState={errorState}
          remainingCredits={remainingTextToImageUsages}
          mediaTypePrompt={mediaTypePrompt}
          updateMediaTypePrompt={setMediaTypePrompt}
        />
        {getAIImageGrid()}
      </div>
    );
  };

  const onBackButton: MouseEventHandler = (e) => {
    if (props.onBack) {
      props.onBack(e);
    }
  };

  const shouldShowHeader = (): boolean => {
    if (isMobile) {
      return true;
    }
    return !!props.onBack;
  };

  const onImageClicked = async (items: GridItemStorage[]): Promise<void> => {
    onClose();
    showLoading('addStockMediaItem', {
      text: window.i18next.t('pmwjs_adding_1_of_1'),
    });

    let recentStockItemCount = 0;

    for (const item of items) {
      if (item.id in gridItemIdsProcessingRef.current) {
        continue;
      }

      gridItemIdsProcessingRef.current[item.id] = true;
      dispatch(
        updateGridItem({
          gridId: gridIDRef.current,
          itemId: item.id,
          changes: {
            isLoading: true,
          },
        })
      );
      try {
        dispatch(updateIsUploadingToMyUploads(true));

        const onAddItemResponse = await uploadSelectedItem(item, props.shouldLoadDataUrlImage);
        dispatch(updateIsUploadingToMyUploads(false));
        props.onAddSelected([onAddItemResponse]);

        if (populateRecentlyAddedOnItemAdd && isUserPremium()) {
          recentStockItemCount = await setRecentStockItem(item);
        }
      } catch (e) {
        console.error(e);

        openModal(MODAL_IDS.ERROR_MODAL_ID, {
          message: window.i18next.t('pmwjs_general_ajax_error'),
          log: false,
        });
      }

      dispatch(
        updateGridItem({
          gridId: gridIDRef.current,
          itemId: item.id,
          changes: {
            isLoading: false,
          },
        })
      );

      delete gridItemIdsProcessingRef.current[item.id];
    }

    dispatch(setRecentStockAvailability(recentStockItemCount >= MIN_STOCK_ITEMS_TO_DISPLAY));
    hideLoading('addStockMediaItem');
  };

  const getPrimaryAction = (): ReactElement<any>[] => {
    return [
      <PanelFooterPrimaryDefaultAction
        key="stock-btn-cta-footer"
        text={window.i18next.t('pmwjs_add_selected')}
        onClick={() => {
          void onImageClicked(isMobile ? selectedItems : [selectedItems[0]]);
        }}
      />,
    ];
  };

  const getDimensionsForAiImageSkeletonItem = (aspectRatio: string): Partial<GridSkeletonItemStorage> => {
    switch (aspectRatio) {
      case AspectRatio.PORTRAIT:
        return {width: 40};
      case AspectRatio.LANDSCAPE:
        return {width: 122};
      case AspectRatio.SQUARE:
      default:
        return {width: 70};
    }
  };

  const onGeneratedImagesReceived = (response: AITextToImageBackendData[]): void => {
    const items: GridAiImageItemStorage[] = response.map(getGridItemForAIGeneratedImageV2);

    items.forEach((gridItem): void => {
      const skeletonId = aiSkeletonItemIds.current.shift();
      if (skeletonId) {
        dispatch(replaceGridItem({gridId: gridIDRef.current, itemId: skeletonId, gridItem}));
      }
    });

    setErrorState(TextToImageGridErrorState.NONE);
  };

  const addLoadingSkeletonsToGrid = (aspectRatio: string): void => {
    const skeletonItemIds = [uuidv4(), uuidv4()];
    const items = [
      {...getAiSkeletonItem(skeletonItemIds[0]), ...getDimensionsForAiImageSkeletonItem(aspectRatio), animationType: SkeletonAnimationType.AI},
      {...getAiSkeletonItem(skeletonItemIds[1]), ...getDimensionsForAiImageSkeletonItem(aspectRatio), animationType: SkeletonAnimationType.AI}
    ]
    dispatch(
      addItemsToGrid({
        id: gridIDRef.current,
        index: 0,
        items: items,
      })
    );
    aiSkeletonItemIds.current.push(...skeletonItemIds);
  };

  const removeLoadingSkeletonsFromGrid = (): void => {
    dispatch(
      deleteGridItems({
        gridId: gridIDRef.current,
        gridSelectionGroupId: selectionIDRef.current,
        itemIdsToDelete: aiSkeletonItemIds.current.slice(2),
      })
    );
    aiSkeletonItemIds.current.shift();
    aiSkeletonItemIds.current.shift();
  };

  const onGenerateImageSuccess = (generateImagesResponse: GenerateAIImageResponse): void => {
    onGeneratedImagesReceived(generateImagesResponse.generatedImages);
    setRemainingTextToImageUsages(generateImagesResponse.remainingCredits);
    setErrorState(TextToImageGridErrorState.NONE);
  };

  const handleInsufficientCreditsError = (prompt: string, promptOptions: SelectedPromptOptionValues): void => {
    removeLoadingSkeletonsFromGrid();

    window.PMW.util.require(typeof window.PMW.stripeCheckout === 'undefined', 'stripeCheckoutDialog', true, () => {
      window.PMW.stripeCheckout.showStripeCheckoutForCreditPackPurchase(
        (data: BuyCreditsResponse) => {
          if (data.credits > 0) {
            void onGenerateImage(prompt, promptOptions);
          }
        },
        noop,
        window.i18next.t('pmwjs_purchase_more_credits_continue_using_ai_images_bg_remover_and_more'),
        window.i18next.t('pmwjs_youre_out_of_credits')
      );
    });
  };

  const onGenerateImageError = (e: Error, prompt: string, promptOptions: SelectedPromptOptionValues): void => {
    setErrorState((): TextToImageGridErrorState => {
      if (e.message === GENERATE_IMAGE_INSUFFICIENT_CREDITS_RESPONSE_CODE) {
        handleInsufficientCreditsError(prompt, promptOptions);
        return TextToImageGridErrorState.NONE;
      }
      if (e.message === GENERATE_IMAGE_FLAGGED_PROMPT_RESPONSE_CODE) {
        return TextToImageGridErrorState.FLAGGED;
      }
      if (e.message === GENERATE_IMAGE_VAGUE_PROMPT_RESPONSE_CODE) {
        return TextToImageGridErrorState.VAGUE;
      }
      if (e.message === GENERATE_IMAGE_UNSUPPORTED_LANGUAGE_RESPONSE_CODE) {
        return TextToImageGridErrorState.UNSUPPORTED_LANUAGE;
      }
      return TextToImageGridErrorState.GENERIC;
    });
  };

  const onGenerateImage = async (prompt: string, promptOptions: SelectedPromptOptionValues): Promise<void> => {
    if (!isUserPremium()) {
      void showTextToImageUpsellDialog();
      return;
    }

    if (prompt.length) {
      setErrorState(TextToImageGridErrorState.NONE);
      dispatch(updateAiImageState(AiImageState.GENERATING));
      let aspectRatio: string = AspectRatio.SQUARE;
      let selectedAIImageStyle = '';
      const promptOptionsKeys = Object.keys(promptOptions) as PromptOption[];
      promptOptionsKeys.forEach((key: PromptOption) => {
        if (key === PromptOption.ASPECT_RATIO) {
          aspectRatio = promptOptions[key];
        }
        if (key === PromptOption.STYLES) {
          selectedAIImageStyle = promptOptions[key];
        }
      });
      addLoadingSkeletonsToGrid(aspectRatio);

      trackPosterBuilderGA4Events(GA4EventName.AI_IMAGE_MENU_GENERATE, {
        [GA4EventParamName.TYPE]: selectedAIImageStyle ?? '',
      });

      const generateImageParams: GenerateImageParams = {
        prompt,
        style: selectedAIImageStyle,
        aspectRatio: aspectRatio,
        category: mediaTypePrompt.valueOf(),
        providerId: selectedAiModelId !== AiImageModelOption.Auto ? selectedAiModelId : undefined,
      };
      try {
        const generateImageResponse = await generateImage(generateImageParams);
        onGenerateImageSuccess(generateImageResponse);
      } catch (e) {
        if (e instanceof Error) {
          onGenerateImageError(e, prompt, promptOptions);
        } else {
          setErrorState(TextToImageGridErrorState.GENERIC);
        }
        removeLoadingSkeletonsFromGrid();
      } finally {
        dispatch(updateAiImageState(AiImageState.READY));
        setTimeout(() => {
          dispatch(updateAiImageState(AiImageState.IDLE));
        }, 2000);
      }
    }
  };

  return (
    <Panel
      className={`_full-width _full-height spacing-p-b-4 ${props.className ?? ''}`}
      panelHeader={
        shouldShowHeader() ? (
          <PanelHeader
            backButtonIconSize={!isMobile ? IconSize.SIZE_ICON_16 : undefined}
            titleClasses={`_fit-width ${styles.aiImageTitleText}`}
            className={`_borderless ${isMobile ? 'text-center' : 'spacing-p-l-4 spacing-p-t-4 spacing-m-b-4'}`}
            size={HeaderSize.SMALL}
            title={window.i18next.t('pmwjs_ai_images')}
            showCloseButton={isMobile}
            onClose={isMobile ? onClose : undefined}
            showBackButton
            onBackButton={onBackButton}
          />
        ) : undefined
      }
      panelContent={<PanelContent className={`_full-width flex-v-row ${styles.panelContent}`}>{getPanelContent()}</PanelContent>}
      panelFooter={selectedItems.length ? <PanelFooter key={selectionIDRef.current} primaryActions={getPrimaryAction()} border fullWidthPrimaryActions /> : undefined}
    />
  );
}
